import { ArrowRightOnRectangleIcon, BeakerIcon, BoltSlashIcon, BookOpenIcon, EyeSlashIcon, KeyIcon, QuestionMarkCircleIcon, UserGroupIcon, WrenchIcon } from '@heroicons/react/20/solid';
import moment from 'moment';
import React from 'react';
import { PRESIDENT, QUORUM_OF_THE_TWELVE } from './constants';
import { checkWasApostle } from './presentPositionCallDate';
import Tooltip from './Tooltip';
import { DetailLevel, Leader, TAG_LEVELS } from './types';

export const evolutionIcons: Record<string, { Icon: typeof BeakerIcon, caption: string }> = {
  accepted: { Icon: BeakerIcon, caption: 'Accepted evolution' },
  open: { Icon: QuestionMarkCircleIcon, caption: 'Open on evolution' },
  rejected: { Icon: WrenchIcon, caption: 'Rejected evolution' },
};

interface LegendEntry {
  Icon: typeof KeyIcon,
  label: string,
  iconClass?: string,
}

// the icon key lives beside the icon logic so the two can't drift
export const iconLegend: LegendEntry[] = [
  { Icon: KeyIcon, label: 'Current prophet', iconClass: 'text-yellow-500' },
  { Icon: KeyIcon, label: 'Was prophet' },
  { Icon: UserGroupIcon, label: 'Polygamist' },
  { Icon: EyeSlashIcon, label: 'Never was an apostle' },
  { Icon: BoltSlashIcon, label: 'Apostle but never in Q12' },
  { Icon: ArrowRightOnRectangleIcon, label: 'Released early' },
];

export const fullIconLegend: LegendEntry[] = [
  { Icon: BookOpenIcon, label: 'College educated' },
  ...Object.values(evolutionIcons).map(({ Icon, caption }) => ({ Icon, label: caption })),
];

interface Props {
  leader: Leader,
  detailLevel: DetailLevel,
}

const Icons = ({ leader, detailLevel }: Props) => {
  const wasApostle = checkWasApostle(leader);
  const wasQ12 = !!leader.positions.find(position => position.type === QUORUM_OF_THE_TWELVE);
  const wasProphet = !!leader.positions.find(position => position.type === PRESIDENT);

  return (
    <>
      {leader.polygamist && <div className="mr-1">
        <Tooltip caption={`Married ${leader.polygamist} women`}>
          <UserGroupIcon className="h-4 w-4" aria-hidden="true" />
        </Tooltip>
      </div>}
      {!wasApostle && <div className="mr-1">
        <Tooltip caption="Never was an apostle">
          <EyeSlashIcon className="h-4 w-4" aria-hidden="true" />
        </Tooltip>
      </div>}
      {wasApostle && !wasQ12 && !wasProphet && <div className="mr-1">
        <Tooltip caption="Apostle but never in Q12">
          <BoltSlashIcon className="h-4 w-4" aria-hidden="true" />
        </Tooltip>
      </div>}
      {leader.presentPosition === PRESIDENT &&
        <div className="mr-1">
          <Tooltip caption="Current Prophet">
            <KeyIcon className="h-4 w-4 text-yellow-500" aria-hidden="true" />
          </Tooltip>
        </div>
      }
      {leader.presentPosition !== PRESIDENT && wasProphet &&
        <Tooltip caption="Prophet">
          <KeyIcon className="h-4 w-4" aria-hidden="true" />
        </Tooltip>
      }
      {wasApostle && leader.deathDate && !moment(leader.deathDate).isSame(leader.releaseDate) &&
        <Tooltip caption="Released early">
          <ArrowRightOnRectangleIcon className="h-4 w-4" aria-hidden="true" />
        </Tooltip>
      }
      {TAG_LEVELS.includes(detailLevel) && leader.collegeEducated && <div className="ml-1">
        <Tooltip caption="College educated">
          <BookOpenIcon className="h-4 w-4" aria-hidden="true" />
        </Tooltip>
      </div>}
      {TAG_LEVELS.includes(detailLevel) && leader.evolution && evolutionIcons[leader.evolution] && <div className="ml-1">
        <Tooltip caption={evolutionIcons[leader.evolution].caption}>
          {React.createElement(evolutionIcons[leader.evolution].Icon, { className: 'h-4 w-4', 'aria-hidden': true })}
        </Tooltip>
      </div>}

      {/* For placeholder spacing */}
      <EyeSlashIcon className="h-4 w-4" aria-hidden="true" style={{ visibility: 'hidden' }} />
    </>
  );
};

export default Icons;